import axios from 'axios';
import type { AxiosInstance } from 'axios';
import type { User, UserRole } from '../types/index';
import { API_CONFIG } from '../constants/config';
import logger from '../utils/logger';

// Auth types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthTokens {
  token: string;
  refreshToken: string;
}

export interface AuthResponse {
  user: User;
  tokens?: AuthTokens; // Optional now - tokens come via HTTP-only cookies
}

export interface AuthError {
  type: 'INVALID_CREDENTIALS' | 'NETWORK_ERROR' | 'SERVER_ERROR' | 'TOKEN_EXPIRED' | 'UNAUTHORIZED';
  message: string;
  statusCode?: number;
}

// Backend response when using HTTP-only cookies
interface LoginApiResponse {
  message: string;
  username: string;
  fullname: string;
}

class AuthService {
  private readonly api: AxiosInstance;
  private refreshPromise: Promise<void> | null = null;
  private readonly baseURL: string;
  private tokenRefreshInterval: number | null = null;
  private isAuthenticated_: boolean = false;
  private currentUser: User | null = null;

  constructor() {
    // Construct base URL for API calls
    // In development, use relative URLs to leverage Vite proxy (same-origin cookies)
    // In production, use the full backend URL (requires backend cookie config)
    const isDev = import.meta.env.DEV;

    if (isDev) {
      // Use Vite proxy - requests go to localhost/api/v1 and are proxied to backend
      // This ensures cookies are same-origin and persist across page refreshes
      this.baseURL = '/api/v1';
    } else {
      const baseUrl = API_CONFIG.BASE_URL || 'https://admin.grandpittu.lk';
      this.baseURL = `${baseUrl}${API_CONFIG.SLUG}`;
    }

    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
      headers: {
        'Content-Type': 'application/json',
      },
      // CRITICAL: Include credentials (cookies) with every request
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  private setupPeriodicTokenRefresh(): void {
    // Clear any existing interval
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }

    // Set up periodic token refresh every 10 minutes
    // Backend handles token via HTTP-only cookies
    this.tokenRefreshInterval = window.setInterval(async () => {
      if (this.isAuthenticated_) {
        try {
          logger.log('Periodic token refresh attempting...');
          await this.refreshAccessToken();
        } catch (error) {
          console.warn('Periodic token refresh failed:', error);
        }
      }
    }, 10 * 60 * 1000); // 10 minutes
  }

  // Clean up interval when service is destroyed
  public destroy(): void {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }
  }

  private setupInterceptors(): void {
    // Request interceptor - no need to add auth header manually
    // HTTP-only cookies are automatically sent by the browser
    this.api.interceptors.request.use(
      (config) => {
        // Ensure withCredentials is always set
        config.withCredentials = true;
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;

        // Don't retry auth endpoints
        if (original?.url?.includes('/auth/')) {
          return Promise.reject(error);
        }

        // Handle token expiration
        if (error.response?.status === 401 && !original._retry) {
          original._retry = true;

          try {
            await this.refreshAccessToken();
            // Retry with same config - cookies will be sent automatically
            return this.api.request(original);
          } catch (refreshError) {
            // Refresh failed, redirect to login
            this.handleAuthFailure();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Authenticate user with credentials
   * Backend sets tokens via HTTP-only cookies
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await this.api.post<LoginApiResponse>('/auth/authendicate', {
        email: credentials.email,
        password: credentials.password
      });

      const { username, fullname } = response.data;

      // Mark as authenticated
      this.isAuthenticated_ = true;

      // Build user object from response
      // Note: We'll get more details on subsequent API calls or from a profile endpoint
      const user: User = {
        id: username, // Use email as ID temporarily
        email: username,
        name: fullname || username.split('@')[0],
        role: 'super-admin' as UserRole, // Will be updated from profile
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        permissions: []
      };

      this.currentUser = user;

      // Store user in sessionStorage for persistence across page refreshes
      // (sessionStorage clears when browser is closed)
      sessionStorage.setItem('auth_user', JSON.stringify(user));

      // Start periodic token refresh
      this.setupPeriodicTokenRefresh();

      // Try to get full profile
      try {
        const fullUser = await this.getProfile();
        this.currentUser = fullUser;
        // Update stored user with full profile
        sessionStorage.setItem('auth_user', JSON.stringify(fullUser));
        return { user: fullUser };
      } catch (profileError) {
        console.warn('Could not fetch full profile, using basic info:', profileError);
        return { user };
      }
    } catch (error: unknown) {
      throw this.mapError(error);
    }
  }

  /**
   * Register new admin user
   */
  async register(userData: {
    firstname: string;
    lastname: string;
    email: string;
    password: string;
  }): Promise<void> {
    try {
      // Create a new axios instance without interceptors for registration
      const registerApi = axios.create({
        baseURL: this.baseURL,
        timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });

      await registerApi.post('/auth/admin/register', userData);
    } catch (error: unknown) {
      throw this.mapError(error);
    }
  }

  /**
   * Verify OTP for account activation
   */
  async verifyOTP(email: string, otp: string): Promise<boolean> {
    try {
      // Create a new axios instance without interceptors for OTP verification
      const verifyApi = axios.create({
        baseURL: this.baseURL,
        timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });

      const response = await verifyApi.get('/auth/activate-account', {
        params: { email, token: otp }
      });
      return response.status === 200;
    } catch (error: unknown) {
      throw this.mapError(error);
    }
  }

  /**
   * Resend OTP for account activation
   */
  async resendOTP(email: string): Promise<void> {
    try {
      // Create a new axios instance without interceptors for resend OTP
      const resendApi = axios.create({
        baseURL: this.baseURL,
        timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });

      // Use path parameter instead of body parameter
      await resendApi.post(`/auth/resend-otp/${email}`);
    } catch (error: unknown) {
      throw this.mapError(error);
    }
  }

  /**
   * Refresh access token using refresh token (via HTTP-only cookie)
   * Backend automatically reads refresh token from cookie
   */
  public async refreshAccessToken(): Promise<void> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.doRefreshToken();

    try {
      await this.refreshPromise;
    } catch (error) {
      // Clear the promise on error so future requests can try again
      this.refreshPromise = null;
      throw error;
    } finally {
      // Only clear the promise if it's still the same one we set
      if (this.refreshPromise) {
        this.refreshPromise = null;
      }
    }
  }

  private async doRefreshToken(): Promise<void> {
    try {
      // Backend reads refresh token from HTTP-only cookie
      // No need to send token in body
      await this.api.post('/auth/refresh-token');

      // If successful, backend sets new tokens via HTTP-only cookies
      logger.log('Token refresh successful');
    } catch (error: unknown) {
      console.error('Token refresh failed:', error);
      this.isAuthenticated_ = false;
      this.currentUser = null;
      throw this.mapError(error);
    }
  }

  /**
   * Change user password
   */
  async changePassword(oldPassword: string, newPassword: string, email?: string): Promise<void> {
    try {
      await this.api.post('/auth/changedPassword', {
        oldPassword,
        newPassword,
        email
      });
    } catch (error: unknown) {
      throw this.mapError(error);
    }
  }

  /**
   * Get current user profile
   * Uses /auth/me or decodes from a profile endpoint
   */
  async getProfile(): Promise<User> {
    try {
      // Try to get profile from a /me or /profile endpoint if available
      // If not, use the stored user info
      if (this.currentUser) {
        return this.currentUser;
      }

      // Fallback: Try to make a simple authenticated request to verify auth
      // and return cached user
      throw new Error('No user profile available - please login');
    } catch (error: unknown) {
      console.error('Error getting user profile:', error);
      throw this.mapError(error);
    }
  }

  /**
   * Logout user and clear session
   * Backend will clear HTTP-only cookies
   */
  async logout(): Promise<void> {
    try {
      // Call backend logout - backend will clear HTTP-only cookies
      await this.api.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if backend call fails
      console.warn('Logout API call failed:', error);
    } finally {
      // Clear local state
      this.isAuthenticated_ = false;
      this.currentUser = null;

      // Clear periodic refresh
      if (this.tokenRefreshInterval) {
        clearInterval(this.tokenRefreshInterval);
        this.tokenRefreshInterval = null;
      }

      // Clear any legacy localStorage items
      try {
        localStorage.removeItem('last_user_activity');
        localStorage.removeItem('admin_access_token');
        localStorage.removeItem('admin_refresh_token');
        sessionStorage.removeItem('admin_access_token');
        sessionStorage.removeItem('auth_user');
      } catch (e) {
        console.warn('Failed to clear legacy storage:', e);
      }

      // Emit auth logout event to notify other parts of the app
      window.dispatchEvent(new CustomEvent('auth:logout', {
        detail: { reason: 'user_initiated' }
      }));
    }
  }

  /**
   * Check if user is authenticated
   * With HTTP-only cookies, we check our internal state
   */
  isAuthenticated(): boolean {
    return this.isAuthenticated_;
  }

  /**
   * Get current access token
   * With HTTP-only cookies, tokens are not accessible from JavaScript
   * This method is kept for backward compatibility but returns null
   */
  getAccessToken(): string | null {
    // HTTP-only cookies cannot be read from JavaScript
    // Return null - callers should not rely on this
    return null;
  }

  /**
   * Handle authentication failures
   */
  public handleAuthFailure(): void {
    this.isAuthenticated_ = false;
    this.currentUser = null;

    // Clear periodic refresh
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }

    // Emit auth failure event
    window.dispatchEvent(new CustomEvent('auth:logout', {
      detail: { reason: 'token_expired' }
    }));

    // Redirect to login if not already there
    if (!window.location.pathname.includes('/login')) {
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?returnUrl=${returnUrl}`;
    }
  }

  /**
   * Map API errors to auth errors
   */
  private mapError(error: unknown): AuthError {
    if (!error || typeof error !== 'object' || !('response' in error)) {
      return {
        type: 'NETWORK_ERROR',
        message: 'Network error occurred. Please check your connection.',
      };
    }

    const axiosError = error as { response?: { status?: number; data?: { error?: { message?: string }; message?: string } } };

    if (!axiosError.response) {
      return {
        type: 'NETWORK_ERROR',
        message: 'Network error occurred. Please check your connection.',
      };
    }

    const { status, data } = axiosError.response;

    switch (status) {
      case 401:
        return {
          type: 'INVALID_CREDENTIALS',
          message: data?.error?.message || data?.message || 'Invalid email or password.',
          statusCode: status,
        };
      case 403:
        return {
          type: 'UNAUTHORIZED',
          message: 'You are not authorized to perform this action.',
          statusCode: status,
        };
      case 404:
        return {
          type: 'INVALID_CREDENTIALS',
          message: 'Account not found.',
          statusCode: status,
        };
      case 429:
        return {
          type: 'SERVER_ERROR',
          message: 'Too many requests. Please try again later.',
          statusCode: status,
        };
      default:
        return {
          type: 'SERVER_ERROR',
          message: data?.error?.message || data?.message || 'An unexpected error occurred.',
          statusCode: status,
        };
    }
  }

  /**
   * Initialize auth state
   * With HTTP-only cookies, we try to make an authenticated request
   * to check if cookies are valid.
   * 
   * NOTE: Backend does not have a /auth/validate endpoint.
   * We try to use a lightweight protected endpoint instead.
   * If no valid cookies exist (fresh visit), user must login.
   */
  async initializeAuth(): Promise<User | null> {
    logger.log('🔄 Initializing auth with HTTP-only cookies...');

    try {
      // Try to make an authenticated request to verify cookies are valid
      // Using a lightweight endpoint that requires auth
      // If this fails with 401, cookies are invalid/expired
      const response = await this.api.get('/floor/getAll');

      if (response.status === 200) {
        // Cookies are valid - user is authenticated
        // But we don't have user details, so create a minimal user
        // The actual user data was stored after login
        this.isAuthenticated_ = true;

        // Try to get stored user from sessionStorage (session-only persistence)
        const storedUser = sessionStorage.getItem('auth_user');
        if (storedUser) {
          try {
            this.currentUser = JSON.parse(storedUser);
            this.setupPeriodicTokenRefresh();
            logger.log('✅ Auth initialized from session storage');
            return this.currentUser;
          } catch {
            // Invalid JSON, continue with minimal user
          }
        }

        // If no stored user, create minimal user
        const user: User = {
          id: 'authenticated-user',
          email: '',
          name: 'Admin User',
          role: 'admin' as UserRole,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          permissions: []
        };

        this.currentUser = user;
        this.setupPeriodicTokenRefresh();
        logger.log('✅ Auth initialized (cookies valid)');
        return user;
      }

      return null;
    } catch (error: unknown) {
      // Check if it's a 401/403 - means cookies are invalid
      const axiosError = error as { response?: { status: number } };

      if (axiosError.response?.status === 401 || axiosError.response?.status === 403) {
        logger.log('ℹ️ No valid auth cookies found - user needs to login');
        this.isAuthenticated_ = false;
        this.currentUser = null;
        sessionStorage.removeItem('auth_user');
        return null;
      }

      // For other errors (network, 500, etc.), assume not authenticated
      logger.log('⚠️ Auth check failed:', error);
      this.isAuthenticated_ = false;
      this.currentUser = null;
      return null;
    }
  }
}

// Create singleton instance
export const authService = new AuthService();
export default authService;